import { HttpContextToken, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, Router } from '@angular/router';
import { Observable } from 'rxjs';
import { ApiPath } from 'src/app/configs/api-paths';
import { environment } from 'src/environments/environment';
import { ClientCallOriginContextService } from './client-call-origin-context.service';

export const CLIENT_CALL_ORIGIN = new HttpContextToken<string | null>(() => null);

@Injectable()
export class ClientCallOriginInterceptor implements HttpInterceptor {
  private backendApiRoot: { origin: string; apiBasePath: string } | null | undefined;

  constructor(
    private router: Router,
    private clientCallOriginContextService: ClientCallOriginContextService,
  ) {}

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    if (!this.shouldAttachClientOrigin(req)) {
      return next.handle(req);
    }

    const explicitOrigin = req.context.get(CLIENT_CALL_ORIGIN);
    const contextualOrigin = this.clientCallOriginContextService.getCurrentOrigin();
    // Preserve the origin set on a previous pass through the chain (e.g. after a token-refresh retry).
    const existingOrigin = req.headers.get('X-Client-Origin');
    const automaticOrigin = this.buildAutomaticOrigin();
    const callOrigin = explicitOrigin || contextualOrigin || existingOrigin || automaticOrigin;
    if (!callOrigin) {
      return next.handle(req);
    }

    // Keep header size predictable while preserving a meaningful origin label.
    const requestWithOrigin = req.clone({
      setHeaders: {
        'X-Client-Origin': callOrigin.substring(0, 256),
      },
    });

    return next.handle(requestWithOrigin);
  }

  private shouldAttachClientOrigin(req: HttpRequest<any>): boolean {
    const requestUrl = this.parseUrl(req.url);
    const backendApiRoot = this.getBackendApiRoot();
    if (!requestUrl || !backendApiRoot) {
      return false;
    }

    if (requestUrl.origin !== backendApiRoot.origin) {
      return false;
    }

    return requestUrl.pathname === backendApiRoot.apiBasePath || requestUrl.pathname.startsWith(`${backendApiRoot.apiBasePath}/`);
  }

  private parseUrl(url: string): URL | null {
    const baseOrigin = typeof window !== 'undefined' && window.location?.origin ? window.location.origin : 'http://localhost';

    try {
      return new URL(url, baseOrigin);
    } catch {
      return null;
    }
  }

  private getBackendApiRoot(): { origin: string; apiBasePath: string } | null {
    if (this.backendApiRoot !== undefined) {
      return this.backendApiRoot;
    }

    const backendProbeUrl = this.parseUrl(environment.getEndpoint(ApiPath.Public.IS_ALIVE));
    if (!backendProbeUrl) {
      this.backendApiRoot = null;
      return this.backendApiRoot;
    }

    const apiSegmentIndex = backendProbeUrl.pathname.indexOf('/api/');
    const apiBasePath = apiSegmentIndex >= 0 ? backendProbeUrl.pathname.substring(0, apiSegmentIndex + 4) : null;
    if (!apiBasePath) {
      this.backendApiRoot = null;
      return this.backendApiRoot;
    }

    this.backendApiRoot = {
      origin: backendProbeUrl.origin,
      apiBasePath,
    };

    return this.backendApiRoot;
  }

  private buildAutomaticOrigin(): string | null {
    const currentUrl = this.router?.url || 'unknown-route';
    const activeRoute = this.getDeepestPrimaryRoute(this.router?.routerState?.snapshot?.root || null);
    const routeKey = this.getRouteOriginKey(activeRoute);
    const componentName = this.getRouteComponentName(activeRoute);

    if (routeKey) {
      return `${currentUrl} | ${routeKey}`;
    }

    if (componentName) {
      return `${currentUrl} | ${componentName}`;
    }

    return currentUrl;
  }

  private getDeepestPrimaryRoute(route: ActivatedRouteSnapshot | null): ActivatedRouteSnapshot | null {
    if (!route) {
      return null;
    }

    let current: ActivatedRouteSnapshot | null = route;
    let last: ActivatedRouteSnapshot | null = route;
    while (current) {
      last = current;
      current = current.firstChild;
    }

    return last;
  }

  private getRouteOriginKey(route: ActivatedRouteSnapshot | null): string | null {
    let current: ActivatedRouteSnapshot | null = route;
    while (current) {
      const originKey = current.routeConfig?.data?.['clientOrigin'];
      if (typeof originKey === 'string' && originKey.trim().length > 0) {
        return originKey.trim();
      }
      current = current.parent;
    }

    return null;
  }

  private getRouteComponentName(route: ActivatedRouteSnapshot | null): string | null {
    let current: ActivatedRouteSnapshot | null = route;
    while (current) {
      const component = current.routeConfig?.component as { name?: string } | undefined;
      if (component?.name && component.name.trim().length > 0) {
        return component.name.trim();
      }
      current = current.parent;
    }

    return null;
  }
}
